/**
 * PhantomConnectButton Component
 *
 * Button for connecting to Phantom's embedded wallet.
 * Features:
 * - Phantom branding (purple theme)
 * - Embedded wallet (no extension needed)
 * - Loading states
 * - Error handling
 * - Auto-authentication after connection
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { usePhantom } from '@/context/PhantomContext';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

export function PhantomConnectButton() {
  const {
    isPhantomAvailable,
    isPhantomLoading,
    isPhantomConnected,
    phantomError,
    connectPhantom,
    showPhantom
  } = usePhantom();
  const { authenticate } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const hasAuthenticatedRef = useRef(false);
  const shouldAuthenticateRef = useRef(false);

  const handleConnect = async () => {
    // Check if embedded wallet is still loading
    if (isPhantomLoading) {
      toast.info('Phantom wallet is initializing. Please wait a moment...');
      return;
    }

    // Check if there was an error
    if (phantomError) {
      toast.error(`Phantom wallet error: ${phantomError}`);
      return;
    }

    // Check if embedded wallet is available
    if (!isPhantomAvailable) {
      toast.error('Phantom embedded wallet is not available. Please refresh the page and try again.');
      return;
    }

    // If already connected, just show the wallet
    if (isPhantomConnected) {
      logger.log('[PhantomConnectButton] Already connected, showing wallet');
      showPhantom();
      return;
    }

    // Connect to Phantom using the connect method
    logger.log('[PhantomConnectButton] Initiating Phantom connection');
    shouldAuthenticateRef.current = true;
    setIsConnecting(true);

    try {
      await connectPhantom();
      logger.log('[PhantomConnectButton] Connection initiated successfully');
    } catch (error) {
      logger.error('[PhantomConnectButton] Failed to connect:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to connect to Phantom');
      shouldAuthenticateRef.current = false;
      setIsConnecting(false);
    }
  };

  useEffect(() => {
    if (!isPhantomConnected || !shouldAuthenticateRef.current || hasAuthenticatedRef.current) {
      return;
    }

    let cancelled = false;
    hasAuthenticatedRef.current = true;
    shouldAuthenticateRef.current = false;
    setIsConnecting(true);

    const authenticateWallet = async () => {
      try {
        logger.log('[PhantomConnectButton] Running authentication after Phantom connection');
        const success = await authenticate();
        if (!cancelled) {
          if (success) {
            toast.success('Connected to Phantom embedded wallet!');
          }
        }
      } catch (error) {
        if (!cancelled) {
          logger.error('[PhantomConnectButton] Authentication after connect failed:', error);
          toast.error(error instanceof Error ? error.message : 'Authentication failed');
          hasAuthenticatedRef.current = false;
          shouldAuthenticateRef.current = false;
        }
      } finally {
        if (!cancelled) {
          setIsConnecting(false);
        }
      }
    };

    authenticateWallet();

    return () => {
      cancelled = true;
      setIsConnecting(false);
    };
  }, [authenticate, isPhantomConnected]);

  useEffect(() => {
    if (!isPhantomConnected) {
      hasAuthenticatedRef.current = false;
      shouldAuthenticateRef.current = false;
    }
  }, [isPhantomConnected]);

  return (
    <button
      onClick={handleConnect}
      disabled={isConnecting || isPhantomLoading}
      className={cn(
        'px-3 py-2 rounded-lg transition-all duration-200 border',
        'text-sm font-medium tracking-tight',
        'w-full md:w-[160px] h-9 flex items-center justify-center gap-2',
        'transform hover:scale-[1.02] active:scale-[0.98]',
        (isConnecting || isPhantomLoading)
          ? 'opacity-80 cursor-not-allowed'
          : 'hover:shadow-lg cursor-pointer'
      )}
      style={{
        fontFamily: 'Tiny5, monospace',
        backgroundColor: phantomError ? '#DC2626' : '#AB9FF2',
        color: '#FFFDF8',
        borderColor: phantomError ? '#991B1B' : '#8C7EDA',
        boxShadow: phantomError ? '0 0 12px rgba(220,38,38,0.45)' : '0 0 18px rgba(171,159,242,0.45)',
      }}
      title={phantomError || undefined}
    >
      {isPhantomLoading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="truncate">Loading SDK...</span>
        </>
      ) : isConnecting ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="truncate">Connecting...</span>
        </>
      ) : (
        <>
          <img src="/Phantom-Icon_Square.svg" alt="Phantom logo" className="w-6 h-6 flex-shrink-0" />
          <span className="truncate">Open Phantom</span>
        </>
      )}
    </button>
  );
}
